import { useEffect, useState } from 'react';
import { Archive, Download, History, Search, RotateCcw, Info, X } from 'lucide-react';
import {
  ARCHIVE_MAX_TAGS_PER_REQUEST,
  ARCHIVE_RESTORE_MAX_DAYS,
  formatHardwareId,
  type ArchiveApplianceSummary,
  type ArchiveExportRequest,
  type ArchiveOverview,
  type ArchiveRestore,
  type ArchiveTagList,
  type CreateArchiveRestoreRequest,
  type PortalAppliance,
} from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, EmptyState, LoadError, Loading, Meter, PortalHeader, StatCard, Table, formatDate, formatDateTime, timeAgo } from './components';
import { Coverage } from './ServicesPages';

const BASE = (import.meta.env.VITE_CLOUD_URL || '').replace(/\/+$/, '');

const bytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 ** 2 ? `${(n / 1024).toFixed(1)} KB` : n < 1024 ** 3 ? `${(n / 1024 ** 2).toFixed(1)} MB` : `${(n / 1024 ** 3).toFixed(2)} GB`);
const compact = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n));
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (d: number) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
/** Date inputs are calendar days in UTC; the range covers the whole of both days. */
const rangeToIso = (from: string, to: string) => ({ from: `${from}T00:00:00.000Z`, to: new Date(new Date(`${to}T00:00:00.000Z`).getTime() + 86400000).toISOString() });

// ── Tag picker (shared by export and restore) ──────────────────────────────

function TagPicker({ applianceId, selected, onChange }: { applianceId: string; selected: string[]; onChange: (keys: string[]) => void }) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 250);
    return () => window.clearTimeout(t);
  }, [search]);
  const { data, error, loading } = useAsync(
    () => cloudApi.get<ArchiveTagList>(`/api/portal/archive/appliances/${encodeURIComponent(applianceId)}/tags?${new URLSearchParams({ search: debounced, limit: '100' })}`),
    [applianceId, debounced],
  );
  const set = new Set(selected);
  const toggle = (key: string) => onChange(set.has(key) ? selected.filter((k) => k !== key) : [...selected, key].slice(0, ARCHIVE_MAX_TAGS_PER_REQUEST));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <label className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search devices and tags"
            className="w-full pl-8 pr-3 py-2 rounded-[var(--radius-glass-sm)] border border-glass-border-bright bg-white text-sm outline-none focus:border-brand-primary"
          />
        </label>
        <span className="text-xs text-text-muted whitespace-nowrap">
          {selected.length} selected{data ? ` · ${data.total.toLocaleString()} tags` : ''}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto rounded-[var(--radius-glass-sm)] border border-glass-border bg-white divide-y divide-glass-border">
        {loading && !data ? (
          <Loading />
        ) : error ? (
          <p className="p-3 text-sm text-status-fault">{error}</p>
        ) : !data || data.tags.length === 0 ? (
          <p className="p-3 text-sm text-text-muted">No archived tags{debounced ? ' match' : ' yet'}.</p>
        ) : (
          data.tags.map((t) => (
            <label key={t.tagKey} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-ink-muted/60">
              <input type="checkbox" checked={set.has(t.tagKey)} onChange={() => toggle(t.tagKey)} />
              <span className="flex-1 min-w-0">
                <span className="font-medium">{t.tagName}</span> <span className="text-text-muted">· {t.deviceName}</span>
                <span className="block text-[11px] font-mono text-text-muted truncate">{t.tagPath}</span>
              </span>
              <span className="text-[11px] text-text-muted whitespace-nowrap">{t.numeric ? 'numeric' : t.dataType ?? 'text'}</span>
            </label>
          ))
        )}
      </div>
      {data && data.total > data.tags.length && <p className="mt-1 text-xs text-text-muted">Showing the first {data.tags.length} — search to narrow down.</p>}
      {selected.length > 0 && (
        <button type="button" onClick={() => onChange([])} className="mt-1 text-xs font-semibold text-text-muted hover:text-status-fault">
          Clear selection
        </button>
      )}
    </div>
  );
}

const dateInput = 'px-3 py-2 rounded-[var(--radius-glass-sm)] border border-glass-border-bright bg-white text-sm outline-none focus:border-brand-primary';

// ── Export ─────────────────────────────────────────────────────────────────

function ExportPanel({ appliance, rawMaxDays, onClose }: { appliance: ArchiveApplianceSummary; rawMaxDays: number; onClose: () => void }) {
  const [tags, setTags] = useState<string[]>([]);
  const [from, setFrom] = useState(daysAgoIso(1));
  const [to, setTo] = useState(todayIso());
  const [resolution, setResolution] = useState<'raw' | 'hour'>('hour');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const days = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1;

  async function download() {
    if (tags.length === 0) return setError('Select at least one tag.');
    if (resolution === 'raw' && days > rawMaxDays) return setError(`Raw exports cover at most ${rawMaxDays} days. Use hourly summaries or a shorter range.`);
    setBusy(true);
    setError(null);
    try {
      const body: ArchiveExportRequest = { tagKeys: tags, resolution, ...rangeToIso(from, to) };
      const res = await fetch(`${BASE}/api/portal/archive/appliances/${encodeURIComponent(appliance.applianceId)}/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const msg = await res.json().then((j: { error?: string }) => j.error).catch(() => null);
        throw new Error(msg ?? `Export failed (${res.status})`);
      }
      const name = /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `archive-${resolution}-${from}-${to}.csv`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`Export data — ${appliance.name}`}
      actions={
        <button type="button" onClick={onClose} className="p-1 text-text-muted hover:text-text-primary" aria-label="Close export">
          <X size={16} />
        </button>
      }
    >
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <TagPicker applianceId={appliance.applianceId} selected={tags} onChange={setTags} />
        <div className="space-y-4">
          <div>
            <span className="block text-xs font-semibold mb-1.5">Date range (UTC)</span>
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" aria-label="From" className={dateInput} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
              <span className="text-sm text-text-muted">to</span>
              <input type="date" aria-label="To" className={dateInput} value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
            </div>
          </div>
          <fieldset>
            <legend className="block text-xs font-semibold mb-1.5">Detail</legend>
            <div className="space-y-2 text-sm">
              <label className="flex items-start gap-2">
                <input type="radio" name="resolution" checked={resolution === 'hour'} onChange={() => setResolution('hour')} className="mt-1" />
                <span>
                  <b>Hourly summary</b> — count, min, max, average and last value per hour. Numeric and on/off tags; any range.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input type="radio" name="resolution" checked={resolution === 'raw'} onChange={() => setResolution('raw')} className="mt-1" />
                <span>
                  <b>Every recorded value</b> — up to {rawMaxDays} days per export.
                </span>
              </label>
            </div>
          </fieldset>
          {error && <Alert kind="error">{error}</Alert>}
          <Button onClick={() => void download()} disabled={busy || tags.length === 0} className="w-full">
            <Download size={15} /> {busy ? 'Preparing CSV…' : `Download CSV (${tags.length} tag${tags.length === 1 ? '' : 's'}, ${days} day${days === 1 ? '' : 's'})`}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Restore ────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<ArchiveRestore['status'], string> = {
  queued: 'bg-brand-primary/10 text-brand-primary',
  in_progress: 'bg-status-idle/12 text-status-idle',
  done: 'bg-status-running/10 text-status-running',
  failed: 'bg-status-fault/10 text-status-fault',
  canceled: 'bg-ink-muted text-text-muted',
};

function RestorePanel({ appliance, canEdit, onClose }: { appliance: ArchiveApplianceSummary; canEdit: boolean; onClose: () => void }) {
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<ArchiveRestore[]>(`/api/portal/archive/appliances/${encodeURIComponent(appliance.applianceId)}/restores`), [appliance.applianceId]);
  const [from, setFrom] = useState(daysAgoIso(7));
  const [to, setTo] = useState(daysAgoIso(1));
  const [allTags, setAllTags] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const active = (data ?? []).some((r) => r.status === 'queued' || r.status === 'in_progress');

  // Progress comes from the appliance; look again while a job is running.
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => void reload(), 15000);
    return () => window.clearInterval(t);
  }, [active, reload]);

  async function create() {
    if (!allTags && tags.length === 0) return setNotice({ kind: 'error', text: 'Select tags, or restore all tags.' });
    if (!window.confirm(`Restore archived history from ${from} to ${to} back to ${appliance.name}? Values already on the appliance are left as they are; missing ones are filled in.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      const body: CreateArchiveRestoreRequest = { ...rangeToIso(from, to), tagKeys: allTags ? null : tags };
      await cloudApi.post(`/api/portal/archive/appliances/${encodeURIComponent(appliance.applianceId)}/restores`, body);
      setNotice({ kind: 'success', text: 'Restore queued. The appliance picks it up within a few minutes while it is online with archiving on.' });
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function cancel(r: ArchiveRestore) {
    try {
      await cloudApi.post(`/api/portal/archive/restores/${encodeURIComponent(r.id)}/cancel`);
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    }
  }

  return (
    <Card
      title={`Restore to appliance — ${appliance.name}`}
      actions={
        <button type="button" onClick={onClose} className="p-1 text-text-muted hover:text-text-primary" aria-label="Close restore">
          <X size={16} />
        </button>
      }
    >
      <p className="text-sm text-text-secondary -mt-1 mb-4">
        Writes archived values back into this appliance’s historian — for example after replacing its storage. Existing values aren’t duplicated. Up to {ARCHIVE_RESTORE_MAX_DAYS} days per job.
      </p>
      {canEdit && (
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 mb-6">
          <div className="space-y-4">
            <div>
              <span className="block text-xs font-semibold mb-1.5">Date range (UTC)</span>
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" aria-label="Restore from" className={dateInput} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
                <span className="text-sm text-text-muted">to</span>
                <input type="date" aria-label="Restore to" className={dateInput} value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={allTags} onChange={(e) => setAllTags(e.target.checked)} /> All archived tags
            </label>
            {notice && <Alert kind={notice.kind}>{notice.text}</Alert>}
            <Button onClick={() => void create()} disabled={busy} className="w-full">
              <RotateCcw size={15} /> {busy ? 'Queuing…' : 'Queue restore'}
            </Button>
          </div>
          {!allTags ? <TagPicker applianceId={appliance.applianceId} selected={tags} onChange={setTags} /> : <div />}
        </div>
      )}
      {loading && !data ? (
        <Loading />
      ) : error ? (
        <LoadError message={error} onRetry={reload} />
      ) : (data ?? []).length === 0 ? (
        <p className="text-sm text-text-muted">No restores yet.</p>
      ) : (
        <Table head={['Requested', 'Range', 'Tags', 'Status', 'Progress', '']}>
          {(data ?? []).map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-2.5 whitespace-nowrap text-text-secondary">
                {formatDateTime(r.createdAt)}
                <div className="text-xs">{r.requestedBy}</div>
              </td>
              <td className="px-5 py-2.5 whitespace-nowrap">
                {formatDate(r.from)} – {formatDate(r.to)}
              </td>
              <td className="px-5 py-2.5">{r.tagKeys ? r.tagKeys.length : 'All'}</td>
              <td className="px-5 py-2.5">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status.replace('_', ' ')}</span>
                {r.error && <div className="text-xs text-status-fault mt-0.5 max-w-xs">{r.error}</div>}
              </td>
              <td className="px-5 py-2.5 min-w-[10rem]">
                <div className="h-1.5 rounded-full bg-ink-muted overflow-hidden">
                  <div className="h-full bg-brand-primary" style={{ width: `${r.hoursTotal ? Math.round((r.hoursDone / r.hoursTotal) * 100) : 0}%` }} />
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  {r.hoursDone}/{r.hoursTotal} hours · {r.pointsWritten.toLocaleString()} values
                </div>
              </td>
              <td className="px-5 py-2.5 text-right">
                {canEdit && (r.status === 'queued' || r.status === 'in_progress') && (
                  <button type="button" onClick={() => void cancel(r)} className="text-xs font-semibold text-status-fault hover:underline">
                    Cancel
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ArchivePage() {
  usePageTitle('Data Archive');
  const { session } = useSession();
  const canEdit = session?.user.role === 'owner';
  const { data, error, loading, reload } = useAsync(async () => {
    const [overview, appliances] = await Promise.all([cloudApi.get<ArchiveOverview>('/api/portal/archive'), cloudApi.get<PortalAppliance[]>('/api/portal/appliances')]);
    return { overview, appliances };
  });
  const [panel, setPanel] = useState<{ kind: 'export' | 'restore'; applianceId: string } | null>(null);

  if (loading && !data) return <Loading />;
  if (error || !data) return <LoadError message={error ?? 'Could not load the archive.'} onRetry={reload} />;

  const { overview, appliances } = data;
  const ent = overview.entitlement;
  const target = panel ? overview.appliances.find((a) => a.applianceId === panel.applianceId) : undefined;

  return (
    <>
      <PortalHeader
        title="Cloud Data Archive"
        description="An off-site copy of each appliance’s history, uploaded hour by hour. Export any range as CSV, or restore it back to an appliance."
        actions={
          <Button to={`/portal/billing?plan=${ent.active ? 'archive-longterm' : 'archive-standard'}`} size="sm" variant={ent.active ? 'secondary' : 'primary'}>
            <Archive size={14} /> {ent.active ? (overview.restoreAvailable ? 'Manage plan' : 'Longer retention & restore') : 'Subscribe'}
          </Button>
        }
      />
      {!ent.active && (
        <div className="mb-6">
          <Alert kind="info">No archive plan is active. Subscribe to Archive or Cloud Complete; any history already archived is kept for 30 days without a plan.</Alert>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Appliances covered" value={ent.appliances} />
        <StatCard label="Retention" value={ent.active ? `${Math.round(ent.retentionDays / 30.4)} months` : '—'} sub={overview.restoreAvailable ? 'Restore to appliance included' : ent.active ? 'Restore needs Long-Term' : undefined} />
        <StatCard label="Values per appliance / month" value={ent.active ? compact(ent.pointsPerMonth) : '—'} />
      </div>

      {panel && target && (
        <div className="mb-6">
          {panel.kind === 'export' ? (
            <ExportPanel appliance={target} rawMaxDays={overview.rawExportMaxDays} onClose={() => setPanel(null)} />
          ) : (
            <RestorePanel appliance={target} canEdit={canEdit} onClose={() => setPanel(null)} />
          )}
        </div>
      )}

      <div className="space-y-4 mb-8">
        {overview.appliances.length === 0 ? (
          <Card>
            <EmptyState icon={History} title="Nothing archived yet" body="Switch archiving on for an appliance below. Uploads start at its next health report, beginning with the last 7 days." />
          </Card>
        ) : (
          overview.appliances.map((a) => (
            <section key={a.applianceId} className="glass-panel p-5">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold">{a.name}</h2>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.active ? 'bg-status-running/10 text-status-running' : 'bg-ink-muted text-text-muted'}`}>
                      {a.active ? 'Archiving' : a.archiveEnabled ? 'Waiting for plan' : 'Off'}
                    </span>
                    {a.restoresActive > 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-status-idle/12 text-status-idle">Restore running</span>}
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    {a.site || 'No site'} · <span className="font-mono">{formatHardwareId(a.hardwareId)}</span> · last upload {timeAgo(a.lastUploadAt)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" disabled={a.tagCount === 0} onClick={() => setPanel({ kind: 'export', applianceId: a.applianceId })}>
                    <Download size={13} /> Export
                  </Button>
                  {overview.restoreAvailable && (
                    <Button size="sm" variant="secondary" disabled={a.hoursArchived === 0} onClick={() => setPanel({ kind: 'restore', applianceId: a.applianceId })}>
                      <RotateCcw size={13} /> Restore
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div>
                  <div className="text-xs text-text-muted">Covers</div>
                  <div className="font-medium">{a.firstHour ? `${formatDate(a.firstHour)} – ${formatDate(a.lastHour)}` : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Hours archived</div>
                  <div className="font-medium tabular-nums">{a.hoursArchived.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Values</div>
                  <div className="font-medium tabular-nums">{a.pointCount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Tags</div>
                  <div className="font-medium tabular-nums">{a.tagCount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Stored</div>
                  <div className="font-medium tabular-nums">{bytes(a.bytes)}</div>
                </div>
              </div>
              <div className="mt-4">
                <Meter label={`Uploaded this month (${a.month.month})`} used={a.month.pointsUsed} max={a.month.pointsAllowed} unit="values" />
              </div>
            </section>
          ))
        )}
      </div>

      <Coverage kind="archive" appliances={appliances} covered={ent.appliances} onChanged={() => void reload()} />
      <div className="mt-6 flex items-start gap-2.5 text-xs text-text-muted">
        <Info size={14} className="mt-0.5 shrink-0" />
        The appliance needs Cloud health reporting turned on (Appliance → System) and outbound HTTPS to cloud.scada.ink. It uploads each complete hour; if it goes offline, it catches up from its own historian when it reconnects.
      </div>
    </>
  );
}
